/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {useMemo} from 'react';
import {useLocation} from 'react-router-dom';
import PropTypes from 'prop-types';

import {useCategory, useProduct, useProductSearch, useStores} from '@salesforce/commerce-sdk-react';
import {pageType} from '@salesforce/commerce-sdk-react/components';
import Seo from '@salesforce/retail-react-app/app/components/seo';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';
import {useSearchParams} from '@salesforce/retail-react-app/app/hooks';

import {Component as ContentfulComponent} from '@amblique/contentful/app/components/contentful/component';
import {PageContext} from '@amblique/contentful/app/contexts/contentful';
import {buildContentfulPageSeoProps} from '@amblique/king-living/app/utils/seo-utils';

/**
 * Default region renderer — maps each `mainRegionCollection` item to a
 * `<ContentfulComponent>`. Extracted so callers (e.g. the king-living
 * category-landing wrapper) can pass a custom renderer via the
 * `renderRegionItems` prop to wrap the items in a chunked-reveal
 * container without forking this page component.
 *
 * @param {Array<object>} items - filtered `mainRegionCollection.items`
 * @returns {React.ReactNode}
 */
const defaultRenderRegionItems = (items) => items.map((component) => (
  <ContentfulComponent key={component.sys.id} component={component} />
));

/**
 * @param {{
 *   page: Contentful.Page,
 *   renderRegionItems?: (items: Array<object>) => React.ReactNode,
 * }} props
 *
 * @returns {React.ReactElement[]}
 */
export const Page = ({page, renderRegionItems = defaultRenderRegionItems, suppressSeo = false, ...props}) => {
  const {locale} = useMultiSite();
  const currency = locale?.preferredCurrency || 'AUD';
  // ------------------------------------------------------------
  // Product Aspects
  // ------------------------------------------------------------
  const {categoryId, productId, storeId} = page;

  const {
    data: product,
    isLoading: isProductLoading,
    isError: isProductError,
    error: productError,
  } = useProduct({
    parameters: {
      id: productId,
      perPricebook: true,
      expand: [
        'availability',
        'promotions',
        'options',
        'images',
        'prices',
        'variations',
        'set_products',
        'bundled_products',
        'page_meta_tags',
        'links',
        'recommendations'
      ],
      allImages: true,
    },
  }, {
    enabled: !!productId,
    // When shoppers select a different variant (and the app fetches the new data),
    // the old data is still rendered (and not the skeletons).
    keepPreviousData: true,
  });


  // ------------------------------------------------------------
  // Category Aspects
  // ------------------------------------------------------------
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const urlParams = new URLSearchParams(location.search);
  const isSearch = !!urlParams.get('q');
  const categoryRefinement = `cgid=${categoryId}`;

  if (categoryId && !searchParams._refine.includes(categoryRefinement)) {
    searchParams._refine.push(categoryRefinement);
  }

  const {_refine, ...restOfParams} = searchParams;

  const {
    data: productSearchResult,
    isLoading: isProductSearchLoading,
    isFetched: isProductSearchFetched,
    isRefetching: isProductSearchRefetching,
  } = useProductSearch({
    parameters: {
      ...restOfParams,
      perPricebook: true,
      allVariationProperties: true,
      allImages: true,
      expand: [
        'promotions',
        'variations',
        'prices',
        'images',
        'page_meta_tags',
        'custom_properties'
      ],
      refine: _refine,
    },
  }, {
    enabled: !!categoryId,
    keepPreviousData: true,
  });

  const {data: category, error: categoryError} = useCategory({
    parameters: {id: categoryId},
  }, {
    enabled: !isSearch && !!categoryId,
  });


  // ------------------------------------------------------------
  // Store Aspects
  // ------------------------------------------------------------
  const {
    data: storesData,
    isLoading: isStoreLoading,
    isError: isStoreError,
    error: storeError,
  } = useStores({
    parameters: {
      ids: storeId,
    },
  }, {
    enabled: !!storeId,
  });

  // Extract the first store from the response
  const store = useMemo(() => {
    if (!storesData?.data || storesData.data.length === 0) {
      return null;
    }

    return storesData.data[0];
  }, [storesData]);


  // ------------------------------------------------------------
  // SEO Data from Contentful + SCAPI
  // ------------------------------------------------------------
  const seoProps = useMemo(
    () => buildContentfulPageSeoProps({
      category,
      currency,
      page,
      product,
    }),
    [category, currency, page, product]
  );


  // ------------------------------------------------------------
  // Page Context
  // ------------------------------------------------------------
  const pageContext = useMemo(() => {
    const {categoryId, productId, storeId} = page;
    const isDebug = true;
    const isPreview = true;
    const isProductPage = !!productId;
    const isCategoryPage = !!categoryId;
    const isStorePage = !!storeId;

    return {
      page,

      isDebug,
      isPreview,
      isProductPage,
      isCategoryPage,
      isStorePage,

      // Product Aspects
      product,
      isProductLoading,
      isProductError,
      productError,

      // Category Aspects
      productSearchResult,
      isProductSearchLoading,
      isProductSearchFetched,
      isProductSearchRefetching,
      category,
      categoryError,

      // Store Aspects
      store,
      isStoreLoading,
      isStoreError,
      storeError,
    };
  }, [
    page,

    // Product Aspects
    product,
    isProductLoading,
    isProductError,
    productError,

    // Category Aspects
    productSearchResult,
    isProductSearchLoading,
    isProductSearchFetched,
    isProductSearchRefetching,
    category,
    categoryError,

    // Store Aspects
    store,
    isStoreLoading,
    isStoreError,
    storeError
  ]);

  // Filter out null/undefined entries (e.g. draft references not resolved by Contentful)
  const regionItems = useMemo(
    () => (page.mainRegionCollection?.items ?? []).filter((item) => item != null && item?.sys?.id),
    [page.mainRegionCollection?.items]
  );

  return (
    <PageContext.Provider value={pageContext}>
      {!suppressSeo && <Seo {...seoProps} />}
      {renderRegionItems(regionItems)}
    </PageContext.Provider>
  );
};

Page.displayName = 'Page';
Page.propTypes = {
  page: pageType,
  suppressSeo: PropTypes.bool,

  /**
   * Optional override for the region-item rendering. When omitted, each
   * `mainRegionCollection` item is rendered as a `<ContentfulComponent>`.
   * Passed the already-filtered (non-null) items array.
   */
  renderRegionItems: PropTypes.func,
};
